import { faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import { Department } from 'src/types/department';
import { DropDownCategories } from 'src/types/generic';

interface BulkUpdateTableToolbarProps {
  isAssign: boolean;
  isMember: boolean;
  selectedRows: any[];
  handleUpdate: VoidFunction;
  handleClearSelection: () => void;
  dropDownList: DropDownCategories[];
  selectionFilter: DropDownCategories;
  handleFilterChange: (event: DropDownListChangeEvent) => void;
  selectedDepartment: Department[];
  isLoading: boolean;
}

const BulkUpdateTableToolbar: FC<BulkUpdateTableToolbarProps> = ({
  isAssign,
  isMember,
  selectedRows,
  handleUpdate,
  handleClearSelection,
  dropDownList,
  selectionFilter,
  handleFilterChange,
  selectedDepartment,
  isLoading,
}) => {
  const { t } = useTranslation();
  const isClearSelectionDisabled = useMemo(() => selectedRows.length === 0, [selectedRows.length]);
  const isMemberUpdateDisable = useMemo(
    () => selectedDepartment.length === 0,
    [selectedDepartment.length]
  );

  const hasError = isMemberUpdateDisable || isClearSelectionDisabled || isLoading;

  return (
    <div className="w-full flex items-center justify-center gap-1 text-white text-sm">
      <Button
        onClick={handleClearSelection}
        disabled={isClearSelectionDisabled}
        className={isClearSelectionDisabled ? '!bg-transparent' : ''}
        endIcon={<FontAwesomeIcon icon={faCircleXmark} />}
        color="transparent"
        size="md"
      >
        {t('Clear Selection')}
      </Button>
      <div className="flex-grow" />
      <DropDownList
        id="view-drop-down-list"
        style={{ width: 200 }}
        data={dropDownList}
        textField="label"
        dataItemKey="value"
        value={selectionFilter}
        onChange={handleFilterChange}
      />
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            {isMemberUpdateDisable && (
              <PopUpContentWrapper
                type={'Error'}
                contents={['Please select one or more departments']}
              />
            )}
            {isClearSelectionDisabled && (
              <PopUpContentWrapper
                type={'Information'}
                contents={[
                  `Please select ${isMember ? 'Member(s)' : 'GL Code(s)'} to ${
                    isAssign ? 'assign' : 'unassign'
                  }`,
                ]}
              />
            )}
          </div>
        }
        show={hasError}
      >
        <Button
          color="secondary"
          onClick={handleUpdate}
          disabled={hasError}
          loading={isLoading}
          size="md"
        >
          {t(
            `${
              isAssign
                ? `Assign Selected ${isMember ? 'Members' : 'GL Codes'}`
                : `Unassign Selected ${isMember ? 'Members' : 'GL Codes'}`
            }`
          )}
        </Button>
      </PopUp>
    </div>
  );
};

export default BulkUpdateTableToolbar;
